/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxProductQuantityViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { BuyboxDimensions } from './custom-components/buybox-dimensions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { MandsModal } from './custom-components/mands-modal';
import CustomCollapse from './custom-components/accordian-custom';

import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/index';
import { IBuyboxProps, IBuyboxResources as IBuyboxExtentionResources } from '../definition-extensions/buybox.ext.props.autogenerated';
import { Quantity } from '../utilities/quantity';
import { ProductWarehouseInventoryAvailability } from '@msdyn365-commerce/retail-proxy';
import VSI_AddPorductInterestAction from '../../../actions/vsi-add-product-interest';
import { createVSI_AddPorductInterestInput } from '../../../custom-retail-extension-proxies/DataActionExtension.g';
import { IBuyboxData as IBuyboxExtensionData} from './buybox.data'

interface IState {
    sizeChartModalIsOpen: boolean;
    isOpencustomAccordian1: boolean;
    isOpencustomAccordian2: boolean;
    isOpencustomAccordian3: boolean;
    priceRange: string | undefined;
    discountedPriceRange: string | undefined;
    showPriceRange: boolean;
    productsAvailability: ProductWarehouseInventoryAvailability[];
    isSizeVariantAvailable: boolean;
    backInStockModalIsOpen: boolean;
    returnText: string | undefined;
    detailsText: string | undefined;
    careInstructionsText: string | undefined;
    compositionText: string | undefined;
    fitText: string | undefined;
    productDetailsText: string | undefined;
    safetyWarningText: string | undefined;
    sleeveLengthText: string | undefined;
    validEmail: boolean | undefined;
    email: string;
    formSubmitted: boolean | undefined;
    quantity: number;
    prevOutOfStockRecordId?: number;
}

/**
 *
 * BuyboxView component
 * @extends {React.PureComponent<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxExtensionData>>}
 */
export class BuyboxView extends React.PureComponent<IBuyboxViewProps & IBuyboxProps<IBuyboxExtensionData>, IState> {
    constructor(props: IBuyboxViewProps & IBuyboxProps<IBuyboxExtensionData>) {
        super(props);
        this.state = {
            sizeChartModalIsOpen: false,
            isOpencustomAccordian1: false,
            isOpencustomAccordian2: false,
            isOpencustomAccordian3: false,
            priceRange: '',
            discountedPriceRange: '',
            showPriceRange: true,
            productsAvailability: [],
            isSizeVariantAvailable: true,
            backInStockModalIsOpen: false,
            returnText: '',
            detailsText: '',
            careInstructionsText: '',
            compositionText: '',
            fitText: '',
            productDetailsText: '',
            safetyWarningText: '',
            sleeveLengthText: '',
            validEmail: true,
            email: '',
            formSubmitted: false,
            quantity: 1
        };
    }

    public componentDidUpdate = async () => {
        await this.getCustomAttribute();
    };

    public componentDidMount = async () => {
        await this.getCustomAttribute();
        document.addEventListener(
            'keydown',
            event => {
                if (event.keyCode === 27) {
                    this.closeAllOpenModals();
                }
            },
            false
        );
    };

    public componentWillUnmount() {
        document.removeEventListener('keydown', this.closeAllOpenModals, false);
    }

    public render(): JSX.Element {
        const {
            ModuleProps,
            MediaGalleryContainerProps,
            ProductInfoContainerProps,
            addToCart,
            addToWishlist,
            description,
            quantity,
            price,
            title,
            data: { productPrice, product },
            callbacks,
            state,
            resources,
            slots,
            config: { freeShipping },
            context: { cultureFormatter }
        } = this.props;

        // tslint:disable-next-line: no-floating-promises
        {
            this.checkQuantity();
        }
        const priceRange = this.state.priceRange ? this.state.priceRange.split('-') : [];
        const discountedPriceRange = this.state.discountedPriceRange ? this.state.discountedPriceRange.split('-') : [];
        const lowerPrice = priceRange.length > 0 ? cultureFormatter.formatCurrency(+priceRange[0]) : undefined;
        const higherPrice = priceRange.length === 2 ? cultureFormatter.formatCurrency(+priceRange[1]) : undefined;
        const showDiscountedPriceRange = this.state.discountedPriceRange ? true : false;
        const discountedLowerPrice =
            discountedPriceRange.length > 0 ? cultureFormatter.formatCurrency(+discountedPriceRange[0]) : undefined;
        const discountedHigherPrice =
            discountedPriceRange.length === 2 ? cultureFormatter.formatCurrency(+discountedPriceRange[1]) : undefined;
        const itemId = this.props.data.product.result ? this.props.data.product.result.ItemId : '';
        const sizeChart = slots.sizeChart && slots.sizeChart.length > 0 ? slots.sizeChart[0] : undefined;

        const variantDiscount =
            productPrice.result && productPrice.result.DiscountAmount && productPrice.result.DiscountAmount > 0
                ? productPrice.result.DiscountAmount
                : 0;
        return (
            <Module {...ModuleProps}>
                {sizeChart && (
                    <MandsModal
                        isOpen={this.state.sizeChartModalIsOpen}
                        modalTitle={this.sizeChartModalTitle()}
                        modalBody={this.sizeChartModalBody()}
                        modalFooter={this.sizeChartModalFooter()}
                        className='size-chart-modal'
                    />
                )}
                <Node {...MediaGalleryContainerProps}>{this.props.mediaGallery}</Node>
                <Node {...ProductInfoContainerProps}>
                    {title}
                    {product.result && <span className='buybox-code-container'>Product code: {product.result.ItemId}</span>}
                    {this.renderPriceRange(
                        showDiscountedPriceRange,
                        discountedPriceRange,
                        priceRange,
                        discountedLowerPrice,
                        discountedHigherPrice,
                        lowerPrice,
                        higherPrice,
                        variantDiscount,
                        price
                    )}
                    {freeShipping && (
                        <div className='free-deliver'>
                            {freeShipping.linkText}{' '}
                            <a role='button' href={freeShipping.linkUrl.destinationUrl}>
                                {}
                            </a>
                        </div>
                    )}
                    <div className='size-guide-container'>
                        <div className='size-guide-ruler' />
                        <span className='size-guide-bold-text' onClick={this.openSizeChartModal} role='button'>
                            Size Chart
                        </span>
                    </div>
                    {this._renderTextBlock(this.props.slots.textBlocks)}
                    <BuyboxDimensions {...this.props} isSizeVariantAvailable={this.state.isSizeVariantAvailable} />
                    {quantity && this._renderQuantity(quantity, callbacks, this.props, state, resources)}
                    {this._renderCartAndWishlistDiv(addToCart, addToWishlist, itemId)}
                    {description}
                    {this._renderAccordian()}
                </Node>
            </Module>
        );
    }

    private renderPriceRange = (
        showDiscountedPriceRange: boolean,
        discountedPriceRange: string[],
        priceRange: string[],
        discountedLowerPrice: string | undefined,
        discountedHigherPrice: string | undefined,
        lowerPrice: string | undefined,
        higherPrice: string | undefined,
        variantDiscount: number,
        price: React.ReactNode
    ) => {
        return (
            <div className='price-range-container'>
                {this.state.showPriceRange ? (
                    showDiscountedPriceRange ? (
                        <div className='mands-discounted-price'>
                            {discountedPriceRange.length !== 2 ? (
                                <span className='mands-discounted-lower-price'>{discountedLowerPrice}</span>
                            ) : (
                                <>
                                    {discountedLowerPrice && <span className='mands-discounted-lower-price'>{discountedLowerPrice}</span>}
                                    {discountedHigherPrice && (
                                        <span className='mands-discounted-higher-price'> - {discountedHigherPrice}</span>
                                    )}
                                </>
                            )}
                            {this.renderDiscountLine(discountedHigherPrice)}
                        </div>
                    ) : (
                        <div className='mands-actual-price'>
                            {priceRange.length !== 2 ? (
                                price
                            ) : (
                                <>
                                    {lowerPrice && <span className='mands-actual-lower-price'>{lowerPrice}</span>}
                                    {higherPrice && <span className='mands-actual-higher-price'> - {higherPrice}</span>}
                                </>
                            )}
                        </div>
                    )
                ) : (
                    <div className={variantDiscount > 0 ? 'mands-discounted-price' : 'mands-actual-price'}>{price}</div>
                )}
            </div>
        );
    };

    private checkQuantity = async () => {
        const currentQuantityCount = this.props.state && this.props.state.quantity;
        const availableQuantity =
            this.props.data &&
            this.props.data.productAvailableQuantity &&
            this.props.data.productAvailableQuantity.result &&
            this.props.data.productAvailableQuantity.result !== undefined &&
            this.props.data.productAvailableQuantity.result[0] !== undefined &&
            this.props.data.productAvailableQuantity.result[0].ProductAvailableQuantity.AvailableQuantity;
        const outOfStockThresholdValue: number = this.props.context.app.config.outOfStockThreshold;

        // if (this.props.data.productAvailableQuantity.result === null) {
        //     this.setState({ isSizeVariantAvailable: true });
        // } else {
        if (availableQuantity && availableQuantity !== undefined && availableQuantity >= outOfStockThresholdValue + currentQuantityCount) {
            this.setState({ isSizeVariantAvailable: true });
        } else {
            // Out of stock
            const { prevOutOfStockRecordId } = this.state;
            const newOutOfStockRecordId = (await this.props.data.product).RecordId;
            if (this.props.data.product.result && !this.props.data.product.result.MasterProductId) {
                this.setState({ isSizeVariantAvailable: true });
            } else if (!prevOutOfStockRecordId) {
                this.setState({ isSizeVariantAvailable: false, prevOutOfStockRecordId: newOutOfStockRecordId });
            } else if (prevOutOfStockRecordId && prevOutOfStockRecordId !== newOutOfStockRecordId) {
                this.setState({
                    isSizeVariantAvailable: false,
                    prevOutOfStockRecordId: newOutOfStockRecordId,
                    backInStockModalIsOpen: false
                });
            }
            // this.setState({ isSizeVariantAvailable: false });
        }

        // }
    };

    private getCustomAttribute = async () => {
        const { data, context } = this.props;
        // @ts-ignore
        const { ItemId } = data.product;
        if (data.product) {
            const recordId = data.product.result && data.product.result.RecordId ? data.product.result.RecordId : undefined;
            const masterProductId =
                data.product.result && data.product.result.MasterProductId
                    ? data.product.result && data.product.result.MasterProductId
                    : undefined;
            const channelId = context.actionContext.requestContext.apiSettings.channelId;
            const callerContext = context.actionContext;
            const catalogId = context.actionContext.requestContext.apiSettings.catalogId;
            let showPriceRange = true;
            if (masterProductId && recordId && masterProductId !== recordId) {
                showPriceRange = false;
                this.setState({ showPriceRange });
            } else if (recordId && showPriceRange) {
                const customAttributes = await getAttributeValuesAsync(
                    { callerContext, queryResultSettings: {} },
                    recordId,
                    channelId,
                    catalogId
                );

                this.setCustomAttributes(customAttributes, showPriceRange);
            }
        }
    };

    private setCustomAttributes = (customAttributes: AttributeValue[], showPriceRange: boolean) => {
        const returnObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Returns')];
        const detailsObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Details')];
        const careInstructionsObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Care Instructions')];
        const compositionObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Composition')];
        const fitObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Fit')];
        const productDetailsObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Product Details')];
        const safetyWarningObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Safety Warning')];
        const sleeveLengthObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Sleeve Length')];
        const priceRangeObj = customAttributes[customAttributes.findIndex(x => x.Name === 'PriceRange')];
        const discountedPriceRangeObj = customAttributes[customAttributes.findIndex(x => x.Name === 'Discounted Price Range')];

        const returnText = returnObj ? returnObj.TextValue : '';
        const detailsText = detailsObj ? detailsObj.TextValue : '';
        const careInstructionsText = careInstructionsObj ? careInstructionsObj.TextValue : '';
        const compositionText = compositionObj ? compositionObj.TextValue : '';
        const fitText = fitObj ? fitObj.TextValue : '';
        const productDetailsText = productDetailsObj ? productDetailsObj.TextValue : '';
        const safetyWarningText = safetyWarningObj ? safetyWarningObj.TextValue : '';
        const sleeveLengthText = sleeveLengthObj ? sleeveLengthObj.TextValue : '';
        const priceRange = priceRangeObj ? priceRangeObj.TextValue : '';
        const discountedPriceRange = discountedPriceRangeObj ? discountedPriceRangeObj.TextValue : '';

        this.setState({
            showPriceRange,
            priceRange,
            discountedPriceRange,
            returnText,
            detailsText,
            careInstructionsText,
            compositionText,
            fitText,
            productDetailsText,
            safetyWarningText,
            sleeveLengthText
        });
    };

    private renderDiscountLine = (higherPrice: string | undefined) => {
        return (
            <>
                {higherPrice && <div className='mands-sale-discount-text'>Sale on selected items</div>}
                {!higherPrice && <div className='mands-sale-discount-text'>Sale</div>}
            </>
        );
    };

    private _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
        if (!textBlocks || textBlocks.length === 0) {
            return undefined;
        }
        return <>{textBlocks[0]}</>;
    };

    private _renderCartAndWishlistDiv = (
        addToCart?: IBuyboxAddToCartViewProps,
        addToWishlist?: IBuyboxAddToWishlistViewProps,
        itemId?: string
    ): JSX.Element | undefined => {
        if (!addToCart && !addToWishlist) {
            return undefined;
        }
        const { isSizeVariantAvailable } = this.state;
        const enableStockCheck = this.props.context.app.config.enableStockCheck;
        const { notifyMeText, contactDetailText, emailLabel, notifyMeButtonName, thankYouText, backInStockText } = this.props.config;
        return (
            <div className='product-add-to-cart'>
                {addToCart && addToCart.errorBlock}
                {addToWishlist && addToWishlist.errorBlock}
                {!isSizeVariantAvailable && (
                    <div className='back-in-stock-link-container'>
                        <p className='backinstock-mainline'>
                            <i className='email-icon' />
                            {notifyMeText}
                        </p>
                        {!this.state.backInStockModalIsOpen && (
                            <div className='backinstock-form'>
                                <p>{contactDetailText}</p>
                                <form>
                                    <input
                                        type='text'
                                        className={
                                            this.state.validEmail ? 'form-control stockEmail' : 'form-control stockEmail invalid-email'
                                        }
                                        placeholder='Email address'
                                        aria-required='true'
                                        maxLength={50}
                                        onChange={this.onChangeEmail}
                                    />
                                    {this.state.formSubmitted && !this.state.validEmail && (
                                        <div className='msc-alert msc-alert-danger'>
                                            <i className='danger-icon' />
                                            {emailLabel}
                                        </div>
                                    )}
                                    <input
                                        required
                                        type='number'
                                        role='textbox'
                                        placeholder='Quantity'
                                        className='form-control'
                                        min='0'
                                        onChange={this.onChangeQuantity}
                                    />
                                    <button type='button' onClick={this.showThankyouNote} className='msc-btn back-in-store-notification'>
                                        {notifyMeButtonName}
                                    </button>
                                </form>
                            </div>
                        )}
                        {this.state.backInStockModalIsOpen && (
                            <div className='backinstock-thankyou'>
                                <div className='thankyou-heading'>
                                    <i className='tick-icon' />
                                    {thankYouText}
                                </div>
                                <p>{backInStockText}</p>
                            </div>
                        )}
                    </div>
                )}

                <div className='buttons'>
                    <div className={`${!isSizeVariantAvailable && enableStockCheck ? 'disabled' : ''}`}>
                        {addToCart && addToCart.button}
                    </div>
                    {addToWishlist && addToWishlist.button}
                </div>
            </div>
        );
    };

    private toggleAccordianState1 = (): void => {
        this.setState({
            isOpencustomAccordian1: !this.state.isOpencustomAccordian1,
            isOpencustomAccordian2: false,
            isOpencustomAccordian3: false
        });
    };

    private showThankyouNote = async () => {
        this.setState({ formSubmitted: true });
        const { email, quantity } = this.state;
        if (!this.validateEmail(email)) {
            this.setState({ validEmail: false });
        } else {
            this.setState({ validEmail: true });
            this.setState({ backInStockModalIsOpen: true });

            const emailSent = false;
            const product = await this.props.data.product;
            const dimensions = product.Dimensions;
            const itemId = product.ItemId;
            const callerContext = this.props.context.actionContext;
            const customerId = '';
            const companyName = 'mas';

            // Size Dimension
            const sizeDimension = dimensions?.filter(dimension => dimension.DimensionTypeValue === 3)[0].DimensionValue?.DimensionId;

            // color Dimension
            const colorDimension = dimensions?.filter(dimension => dimension.DimensionTypeValue === 1)[0].DimensionValue?.DimensionId;

            const fullProductCode = `${itemId}${colorDimension}${sizeDimension}`;

            const variantRecordID = product.RecordId;

            const addProductInterestInput = createVSI_AddPorductInterestInput(
                customerId,
                email,
                fullProductCode,
                quantity.toString(),
                emailSent,
                companyName,
                variantRecordID
            );

            await VSI_AddPorductInterestAction(addProductInterestInput, callerContext);
        }
    };

    private onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        if (!this.validateEmail(email.toString())) {
            this.setState({ validEmail: false, email });
        } else {
            this.setState({ validEmail: true, email });
        }
    };

    private onChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const parseQuantity = parseInt(event.target.value, 10);
        const quantity = parseQuantity ? parseQuantity : 1;
        this.setState({ quantity });
    };

    private validateEmail = (email: string) => {
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    private toggleAccordianState2 = (): void => {
        this.setState({
            isOpencustomAccordian1: false,
            isOpencustomAccordian2: !this.state.isOpencustomAccordian2,
            isOpencustomAccordian3: false
        });
    };

    private toggleAccordianState3 = (): void => {
        this.setState({
            isOpencustomAccordian1: false,
            isOpencustomAccordian2: false,
            isOpencustomAccordian3: !this.state.isOpencustomAccordian3
        });
    };

    private _renderAccordian = () => {
        const {
            returnText,
            detailsText,
            careInstructionsText,
            compositionText,
            // fitText,
            productDetailsText,
            safetyWarningText,
            sleeveLengthText
        } = this.state;
        const careCompositionText = `${careInstructionsText ? `${careInstructionsText}\n\n` : ''}${
            compositionText ? `${compositionText}\n\n` : ''
        }${safetyWarningText ? `${safetyWarningText}\n\n` : ''}${detailsText ? `${detailsText}\n\n` : ''}${
            sleeveLengthText ? `${sleeveLengthText}` : ''
        }`;
        const productInformationText = `${productDetailsText ? `${productDetailsText}\n\n` : ''}`;
        const {
            config: { accordian1Title = '', accordian2Title = '', accordian3Title = '' }
        } = this.props;
        return (
            <div className='product-accordion'>
                {productInformationText && (
                    <CustomCollapse
                        content={productInformationText}
                        title={accordian1Title}
                        type={'product-information'}
                        isOpen={this.state.isOpencustomAccordian1}
                        toggle={this.toggleAccordianState1}
                    />
                )}

                {careCompositionText && (
                    <CustomCollapse
                        content={careCompositionText}
                        title={accordian2Title}
                        type={'care-composition'}
                        isOpen={this.state.isOpencustomAccordian2}
                        toggle={this.toggleAccordianState2}
                    />
                )}

                {returnText && (
                    <CustomCollapse
                        content={returnText}
                        title={accordian3Title}
                        type={'delivery-information'}
                        isOpen={this.state.isOpencustomAccordian3}
                        toggle={this.toggleAccordianState3}
                    />
                )}
            </div>
        );
    };

    private _renderQuantity = (
        quantityComponent: IBuyboxProductQuantityViewProps,
        callbacks: IBuyboxCallbacks,
        props: IBuyboxProps<{}>,
        state: IBuyboxState,
        extentionResources: IBuyboxExtentionResources
    ): JSX.Element => {
        const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

        const {
            resources,
            context: {
                app: {
                    config: { maxQuantityForCartLineItem }
                }
            }
        } = props;

        const { quantity } = state;

        const onChange = (newValue: number) => {
            if (callbacks.updateQuantity) {
                callbacks.updateQuantity(newValue);
            }
        };

        return (
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {errors}
                </Node>

                <Quantity
                    id='ms-buybox__product-quantity-input'
                    max={maxQuantityForCartLineItem || 10}
                    currentCount={quantity}
                    onChange={onChange}
                    inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                    decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                    incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                />
            </Node>
        );
    };

    private closeAllOpenModals = () => {
        this.setState({
            sizeChartModalIsOpen: false
        });
    };

    private sizeChartModalTitle = (): JSX.Element => {
        return (
            <div className='header-close-button' onClick={this.closeSizeChartModal} role='button'>
                <span className='chart-close-icon' />
            </div>
        );
    };

    private sizeChartModalBody = (): JSX.Element => {
        const { slots } = this.props;
        const sizeChart = slots.sizeChart && slots.sizeChart.length > 0 ? slots.sizeChart[0] : undefined;
        return <div className='size-chart-popup'>{sizeChart}</div>;
    };

    private sizeChartModalFooter = (): JSX.Element => {
        return <div className='close-button' role='button' onClick={this.closeSizeChartModal} />;
    };

    private openSizeChartModal = (): void => {
        this.setState({ sizeChartModalIsOpen: true });
    };

    private closeSizeChartModal = (): void => {
        this.setState({ sizeChartModalIsOpen: false });
    };
}

export default BuyboxView;
